/**
 * Transforms timeline events into TimelineItems for rendering.
 *
 * All events are wrapped in a uniform TimelineItem structure:
 * - Non-association events → { type: 'single_event', events: [event] }
 * - Association groups → { type: 'association_changed' | 'association_added' | 'association_removed', events: [...] }
 *
 * Association events are grouped when timestamps are within 200ms AND either:
 * - Same (association_type_name, actor) — e.g. bulk adds by the same user
 * - Causally linked — one event's source_id references another's event_id
 *   (e.g. cardinality enforcement cascade removes the old stage when a new one is added)
 */

import type { AssociationPayload, TimelineEvent } from './types';
import { EVENT_TYPES } from './types';

export const ITEM_TYPES = {
  SINGLE_EVENT: 'single_event',
  ASSOCIATION_CHANGED: 'association_changed',
  ASSOCIATION_ADDED: 'association_added',
  ASSOCIATION_REMOVED: 'association_removed',
  PROPERTY_UPDATED: 'property_updated',
  OBJECT_CREATED: 'object_created',
} as const;

export type ItemType = (typeof ITEM_TYPES)[keyof typeof ITEM_TYPES];

export interface TimelineItem {
  type: ItemType;
  events: TimelineEvent[];
}

/** Maximum time difference (in ms) for events to be grouped together */
const GROUPING_THRESHOLD_MS = 200;

function isAssociationEvent(event: TimelineEvent): boolean {
  return (
    event.event_type_name === EVENT_TYPES.ASSOCIATION_ADDED ||
    event.event_type_name === EVENT_TYPES.ASSOCIATION_REMOVED
  );
}

function getAssociationTypeName(event: TimelineEvent): string | null {
  const payload = event.payload as AssociationPayload | undefined;
  return payload?.association_type_name ?? null;
}

/**
 * Creates a grouping key for association events based on actor + association type.
 */
function getAssociationGroupKey(event: TimelineEvent): string | null {
  if (!isAssociationEvent(event)) {
    return null;
  }

  const assocType = getAssociationTypeName(event);
  if (!assocType) {
    return null;
  }

  // Group by: association type and actor (timestamp checked separately)
  const actorKey = `${event.source_type_name ?? ''}:${event.source_id ?? ''}`;
  return `${assocType}|${actorKey}`;
}

/**
 * Check if two timestamps are within the grouping threshold.
 */
function areTimestampsClose(ts1: string, ts2: string): boolean {
  const time1 = new Date(ts1).getTime();
  const time2 = new Date(ts2).getTime();
  return Math.abs(time1 - time2) <= GROUPING_THRESHOLD_MS;
}

/**
 * Check if an event is causally linked to any event in the group.
 *
 * A causal link exists when either:
 * - The event's source_id matches an event_id in the group, OR
 * - A group event's source_id matches this event's event_id
 *
 * This handles both orderings (added before removed, or removed before added)
 * since the view-state returns events in reverse chronological order.
 */
function isCausallyLinked(
  event: TimelineEvent,
  groupEvents: TimelineEvent[]
): boolean {
  const assocType = getAssociationTypeName(event);
  if (!assocType) {
    return false;
  }

  return groupEvents.some(ge => {
    if (getAssociationTypeName(ge) !== assocType) {
      return false;
    }
    // This event was caused by a group event
    if (event.source_id && String(ge.event_id) === String(event.source_id)) {
      return true;
    }
    // A group event was caused by this event
    if (ge.source_id && String(event.event_id) === String(ge.source_id)) {
      return true;
    }
    return false;
  });
}

/**
 * Converts a group of association events into TimelineItem(s).
 * Returns 1 item for 'changed' (1 removed + 1 added), or up to 2 items
 * for separate add/remove operations.
 */
function createAssociationItems(events: TimelineEvent[]): TimelineItem[] {
  const removed = events.filter(
    e => e.event_type_name === EVENT_TYPES.ASSOCIATION_REMOVED
  );
  const added = events.filter(
    e => e.event_type_name === EVENT_TYPES.ASSOCIATION_ADDED
  );

  const items: TimelineItem[] = [];

  // Check for 'changed' pattern: exactly 1 removed + 1 added
  if (removed.length === 1 && added.length === 1) {
    items.push({
      type: ITEM_TYPES.ASSOCIATION_CHANGED,
      events: [removed[0], added[0]],
    });
  } else {
    // Create separate items for removes and adds
    if (removed.length > 0) {
      items.push({
        type: ITEM_TYPES.ASSOCIATION_REMOVED,
        events: removed,
      });
    }
    if (added.length > 0) {
      items.push({
        type: ITEM_TYPES.ASSOCIATION_ADDED,
        events: added,
      });
    }
  }

  return items;
}

/**
 * Transforms timeline events into TimelineItems.
 *
 * Wraps all events in a uniform structure. Groups consecutive association
 * events that share the same (association_type_name, actor) OR are causally
 * linked, and have timestamps within 200ms of each other.
 *
 * @param events - Array of timeline events
 * @returns Array of TimelineItems
 */
export function transformToTimelineItems(
  events: TimelineEvent[]
): TimelineItem[] {
  const result: TimelineItem[] = [];
  let currentGroupKey: string | null = null;
  let currentGroupEvents: TimelineEvent[] = [];

  function flushCurrentGroup() {
    if (currentGroupEvents.length > 0) {
      const items = createAssociationItems(currentGroupEvents);
      result.push(...items);
      currentGroupEvents = [];
      currentGroupKey = null;
    }
  }

  for (const event of events) {
    const groupKey = getAssociationGroupKey(event);

    if (groupKey === null) {
      // Non-association event - flush any pending group and wrap as single
      flushCurrentGroup();
      result.push({
        type: ITEM_TYPES.SINGLE_EVENT,
        events: [event],
      });
    } else {
      // Check timestamp proximity with current group
      const firstEvent = currentGroupEvents[0];
      const withinThreshold =
        !firstEvent ||
        areTimestampsClose(event.occurred_at, firstEvent.occurred_at);

      if (!withinThreshold) {
        // Too far apart - flush and start new group
        flushCurrentGroup();
        currentGroupKey = groupKey;
        currentGroupEvents.push(event);
      } else if (
        groupKey === currentGroupKey ||
        isCausallyLinked(event, currentGroupEvents)
      ) {
        // Same actor OR caused by an event already in the group - accumulate
        currentGroupEvents.push(event);
      } else {
        // Different group - flush previous and start new
        flushCurrentGroup();
        currentGroupKey = groupKey;
        currentGroupEvents.push(event);
      }
    }
  }

  // Flush any remaining group
  flushCurrentGroup();

  return result;
}
